import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
  ArrowLeft,
  DollarSign,
  Heart,
  Landmark,
  MapPin,
  Share2,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  DONATION_CAMPAIGN_DEADLINE_LABEL,
  DONATION_CAMPAIGN_GOAL_CENTS,
  DONATION_CAMPAIGN_STARTING_FUNDED_PERSON_COUNT,
  DONATION_CAMPAIGN_STARTING_FUNDS_GIVEN_CENTS,
  DONATION_CAMPAIGN_STARTING_DONORS,
  DONATION_CAMPAIGN_STARTING_RAISED_CENTS,
  DONATION_REQUEST_FALLBACK_PEOPLE_STILL_IN_REQUEST,
  DONATION_REQUEST_FALLBACK_STILL_IN_REQUEST_CENTS,
  DONATION_REQUEST_FALLBACK_TOTAL_REQUESTED_CENTS,
} from "@shared/const";

const PRESET_AMOUNTS = [25, 50, 100, 250, 500, 1000];
const ZELLE_EMAIL = "donate@pvchialpha.com";
const CASHAPP_HANDLE = "$pvchialpha";

function formatDollars(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function OtherGivingMethods() {
  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Other ways to give
          </p>
          <p className="text-xs text-slate-500">
            PV Chi Alpha is only hosting the fund; all gifts will be sent out to
            cover CMC needs.
          </p>
        </div>
        <TooltipProvider delayDuration={100}>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Zelle: ${ZELLE_EMAIL}`}
                  onClick={() =>
                    void navigator.clipboard?.writeText(ZELLE_EMAIL)
                  }
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  <Landmark className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div className="font-semibold">Zelle</div>
                  <div className="opacity-80">{ZELLE_EMAIL}</div>
                </div>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href="https://cash.app/$pvchialpha"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Cash App: ${CASHAPP_HANDLE}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  <DollarSign className="h-5 w-5" />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  <div className="font-semibold">Cash App</div>
                  <div className="opacity-80">{CASHAPP_HANDLE}</div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}

interface FundingSummaryCardProps {
  fundsGivenCents: number;
  fundedPersonCount: number;
  totalRaisedCents: number;
  peopleStillNeedFunding: number;
  totalRequestedCents: number;
  totalNeedCents: number;
}

function FundingSummaryCard({
  fundsGivenCents,
  fundedPersonCount,
  totalRaisedCents,
  peopleStillNeedFunding,
  totalRequestedCents,
  totalNeedCents,
}: FundingSummaryCardProps) {
  const percentOfRaised =
    totalRaisedCents > 0
      ? Math.min((fundsGivenCents / totalRaisedCents) * 100, 100)
      : 0;
  const remainingPercent = Math.max(100 - percentOfRaised, 0);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-4 lg:flex-col lg:gap-3">
        <div className="relative size-24 shrink-0" aria-hidden="true">
          <svg className="size-full -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="rgb(226 232 240)"
              strokeWidth="10"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              pathLength="100"
              stroke="rgb(220 38 38)"
              strokeLinecap="round"
              strokeWidth="10"
              strokeDasharray={`${percentOfRaised} ${remainingPercent}`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-lg font-bold text-slate-900">
              {Math.round(percentOfRaised)}%
            </span>
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-normal">
              given
            </span>
          </div>
        </div>
        <div className="min-w-0 flex-1 lg:text-center">
          <p className="text-sm font-semibold text-slate-900">Funds given</p>
          <p className="mt-1 text-2xl font-bold text-red-600">
            {formatDollars(fundsGivenCents)}
          </p>
          <p className="text-sm text-slate-500">
            {fundedPersonCount} {fundedPersonCount === 1 ? "person" : "people"}
            {" funded"}
          </p>
          <div className="mt-3 space-y-2 text-xs text-slate-600">
            <div>
              <span className="block font-semibold text-slate-900">
                {formatDollars(totalRequestedCents)}
              </span>
              help requested
            </div>
            <div>
              <span className="block font-semibold text-slate-900">
                {formatDollars(totalNeedCents)}
              </span>
              still in request by {peopleStillNeedFunding}{" "}
              {peopleStillNeedFunding === 1 ? "person" : "people"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegionFundsSection() {
  const { user } = useAuth();
  const { data: regionFunds } = trpc.regionFunds.summary.useQuery(undefined, {
    enabled: !!user,
  });

  if (!user || !regionFunds || regionFunds.length === 0) return null;

  return (
    <div className="px-6 sm:px-10 py-6 sm:py-8 border-b border-slate-100">
      <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <MapPin className="w-6 h-6 text-red-600" />
        Funds Raised by Region
      </h2>
      <p className="text-slate-600 mb-4">
        See how each region is doing in raising funds for missionary needs.
      </p>
      <div className="space-y-4">
        {regionFunds
          .sort((a, b) => b.totalFundedCents - a.totalFundedCents)
          .map(region => {
            const pct =
              region.totalNeededCents > 0
                ? Math.min(
                    (region.totalFundedCents / region.totalNeededCents) * 100,
                    100
                  )
                : 0;
            return (
              <div key={region.region}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-800">
                    {region.region}
                  </span>
                  <span className="text-sm text-slate-500">
                    {formatDollars(region.totalFundedCents)} /{" "}
                    {formatDollars(region.totalNeededCents)}
                  </span>
                </div>
                <Progress value={pct} className="h-2.5" />
                <div className="flex justify-between mt-0.5">
                  <span className="text-xs text-slate-400">
                    {region.activeNeedCount} active need
                    {region.activeNeedCount !== 1 ? "s" : ""}
                  </span>
                  <span className="text-xs font-medium text-slate-500">
                    {Math.round(pct)}%
                  </span>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default function Donate() {
  const [, setLocation] = useLocation();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(100);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const utils = trpc.useUtils();

  // Check URL params for success/cancel
  const urlParams = new URLSearchParams(window.location.search);
  const isSuccess = urlParams.get("success") === "true";
  const isCanceled = urlParams.get("canceled") === "true";

  // Fetch campaign progress
  const { data: progress, refetch: refetchProgress } =
    trpc.donations.progress.useQuery(undefined, {
      refetchInterval: isSuccess ? 5_000 : 30_000,
      refetchOnWindowFocus: true,
    });

  const createCheckout = trpc.donations.createCheckoutSession.useMutation();

  const amountCents =
    selectedAmount !== null
      ? selectedAmount * 100
      : customAmount
        ? Math.round(parseFloat(customAmount) * 100)
        : 0;

  const goalCents = progress?.goalCents ?? DONATION_CAMPAIGN_GOAL_CENTS;
  const deadlineLabel =
    progress?.deadlineLabel ?? DONATION_CAMPAIGN_DEADLINE_LABEL;
  const totalRaised =
    progress?.totalRaisedCents ?? DONATION_CAMPAIGN_STARTING_RAISED_CENTS;
  const donors =
    progress?.donors ??
    DONATION_CAMPAIGN_STARTING_DONORS.map(d => ({
      name: d.name,
      amountCents: d.amountCents,
      initials: d.initials,
    }));
  const donorCount = progress?.donorCount ?? donors.length;
  const progressPercent = Math.min((totalRaised / goalCents) * 100, 100);
  const fundingSummary = progress?.fundingSummary ?? {
    fundsGivenCents: DONATION_CAMPAIGN_STARTING_FUNDS_GIVEN_CENTS,
    fundedPersonCount: DONATION_CAMPAIGN_STARTING_FUNDED_PERSON_COUNT,
    peopleStillNeedFunding: DONATION_REQUEST_FALLBACK_PEOPLE_STILL_IN_REQUEST,
    totalRequestedCents: DONATION_REQUEST_FALLBACK_TOTAL_REQUESTED_CENTS,
    totalNeedCents: DONATION_REQUEST_FALLBACK_STILL_IN_REQUEST_CENTS,
  };

  const donateUrl =
    typeof window !== "undefined" ? window.location.origin + "/donate" : "";

  async function handleDonate() {
    if (amountCents < 100) return;
    setIsProcessing(true);
    setCheckoutError(null);
    try {
      const result = await createCheckout.mutateAsync({
        amountCents,
        donorName: donorName || undefined,
        donorEmail: donorEmail || undefined,
      });
      if (result.sessionUrl) {
        window.location.href = result.sessionUrl;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutError(
        "We couldn't start checkout. Please try again in a moment."
      );
      setIsProcessing(false);
    }
  }

  async function handleShare() {
    const shareData = {
      title: "Help Missionaries Attend CMC 2026",
      text: "Support missionaries who can't afford to attend Campus Missions Conference. Every dollar counts!",
      url: donateUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled share
      }
    } else {
      await navigator.clipboard.writeText(donateUrl);
      setShowShareSuccess(true);
      setTimeout(() => setShowShareSuccess(false), 2000);
    }
  }

  async function handleShareQRCode() {
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=1024x1024&data=${encodeURIComponent(donateUrl)}`;

    if (navigator.share && navigator.canShare) {
      try {
        const response = await fetch(qrImageUrl);
        const blob = await response.blob();
        const file = new File([blob], "cmc-donate-qr.png", {
          type: "image/png",
        });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "CMC Donation QR Code",
            text: "Scan this QR code to donate:",
            files: [file],
          });
          return;
        }
      } catch {
        // Fall back to opening QR image
      }
    }

    window.open(qrImageUrl, "_blank", "noopener,noreferrer");
  }

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isDocModalOpen) {
          setIsDocModalOpen(false);
          e.preventDefault();
          return;
        }
        if (showQR) {
          setShowQR(false);
          e.preventDefault();
          return;
        }
        setLocation("/");
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDocModalOpen, showQR, setLocation]);

  useEffect(() => {
    if (!isSuccess) return;
    void utils.donations.progress.invalidate();
    void refetchProgress();
  }, [isSuccess, refetchProgress, utils]);

  // Success/Cancel overlay
  if (isSuccess || isCanceled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 max-w-md w-full text-center">
          {isSuccess ? (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-emerald-600 fill-emerald-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Thank You!
              </h1>
              <p className="text-slate-600 text-lg mb-8">
                Your generous donation will help missionaries attend CMC 2026.
                God bless you!
              </p>
              <div className="text-left mb-8 p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-semibold text-slate-900">
                    Campaign Progress
                  </span>
                  <span className="text-sm text-slate-500">
                    Deadline: {deadlineLabel}
                  </span>
                </div>
                <Progress
                  value={progressPercent}
                  className="h-3 mb-2 bg-slate-200 [&>div]:bg-red-600"
                />
                <p className="text-sm text-slate-600">
                  {formatDollars(totalRaised)} raised toward{" "}
                  {formatDollars(goalCents)}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="w-8 h-8 text-slate-500" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Donation Canceled
              </h1>
              <p className="text-slate-600 text-lg mb-8">
                No worries! You can come back anytime to support missionaries.
              </p>
            </>
          )}
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => setLocation("/donate")}
              className="bg-red-600 hover:bg-red-700 text-white h-12 text-base"
            >
              {isSuccess ? "Make Another Donation" : "Try Again"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setLocation("/")}
              className="text-slate-600 h-12"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setLocation("/")}
          className="mb-4 text-slate-600 hover:text-slate-900 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Back to Home</span>
          <span className="sm:hidden">Back</span>
        </Button>

        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          {/* Header banner */}
          <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-700 px-6 sm:px-10 py-8 sm:py-12 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-8 h-8 fill-white" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
                Send a Missionary to CMC
              </h1>
            </div>
            <p className="text-red-100 text-lg sm:text-xl max-w-2xl leading-relaxed">
              Help missionaries who can't afford to attend Campus Missions
              Conference 2026. Every dollar makes a difference.
            </p>
          </div>

          {/* Campaign Goal */}
          <div className="px-6 sm:px-10 py-6 sm:py-8 border-b border-slate-100">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                {formatDollars(goalCents)} Goal
              </h2>
              <span className="text-sm text-slate-500 font-medium">
                Deadline: {deadlineLabel}
              </span>
            </div>
            <div className="mb-3">
              <Progress
                value={progressPercent}
                className="h-4 bg-slate-100 [&>div]:bg-red-600"
              />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
              <div>
                <span className="text-2xl sm:text-3xl font-bold text-slate-900">
                  {formatDollars(totalRaised)}
                </span>
                <span className="text-slate-500 text-base ml-2">
                  of {formatDollars(goalCents)} goal
                </span>
              </div>
              <div className="flex items-center gap-3 sm:justify-end">
                {donors.length > 0 && (
                  <TooltipProvider delayDuration={100}>
                    <div className="flex -space-x-2">
                      {donors.slice(0, 5).map((donor, idx) => (
                        <Tooltip key={`${donor.name}-${idx}`}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={`${donor.name} donated ${formatDollars(donor.amountCents)}`}
                              className="w-8 h-8 rounded-full bg-red-100 text-red-700 border-2 border-white text-xs font-semibold flex items-center justify-center shadow-sm hover:z-10 hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-red-400"
                            >
                              {donor.initials}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <div className="font-semibold">{donor.name}</div>
                              <div className="opacity-80">
                                {formatDollars(donor.amountCents)}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      {donors.length > 5 && (
                        <div
                          aria-label={`${donors.length - 5} more donors`}
                          className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 border-2 border-white text-xs font-semibold flex items-center justify-center shadow-sm"
                        >
                          +{donors.length - 5}
                        </div>
                      )}
                    </div>
                  </TooltipProvider>
                )}
                <div className="text-right">
                  <span className="text-lg font-semibold text-slate-700">
                    {donorCount}
                  </span>
                  <span className="text-slate-500 text-sm ml-1">
                    {donorCount === 1 ? "donor" : "donors"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Donation Form — directly under progress bar */}
          <div className="px-6 sm:px-10 py-8 sm:py-10 border-b border-slate-100">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-6">
              Make a Donation
            </h2>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start mb-6">
              <div>
                {/* Preset Amounts */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
                  {PRESET_AMOUNTS.map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount("");
                      }}
                      className={`
                        py-3 px-2 rounded-xl font-semibold text-base transition-all border-2
                        ${
                          selectedAmount === amount
                            ? "bg-black text-white border-black shadow-md scale-105"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        }
                      `}
                    >
                      ${amount.toLocaleString()}
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">
                    Or enter a custom amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg font-medium">
                      $
                    </span>
                    <Input
                      type="number"
                      min="1"
                      step="any"
                      value={customAmount}
                      onChange={e => {
                        setCustomAmount(e.target.value);
                        setSelectedAmount(null);
                      }}
                      placeholder="Enter amount"
                      className="pl-8 h-12 text-lg border-slate-200 focus:border-red-400 focus:ring-red-400"
                    />
                  </div>
                </div>

                {/* Donor Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">
                      Name (optional)
                    </label>
                    <Input
                      value={donorName}
                      onChange={e => setDonorName(e.target.value)}
                      placeholder="Your name"
                      className="h-11 border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">
                      Email (optional)
                    </label>
                    <Input
                      type="email"
                      value={donorEmail}
                      onChange={e => setDonorEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="h-11 border-slate-200"
                    />
                  </div>
                </div>
              </div>
              <FundingSummaryCard
                fundsGivenCents={fundingSummary.fundsGivenCents}
                fundedPersonCount={fundingSummary.fundedPersonCount}
                totalRaisedCents={totalRaised}
                peopleStillNeedFunding={fundingSummary.peopleStillNeedFunding}
                totalRequestedCents={fundingSummary.totalRequestedCents}
                totalNeedCents={fundingSummary.totalNeedCents}
              />
            </div>

            {/* Donate Button */}
            <Button
              onClick={handleDonate}
              disabled={amountCents < 100 || isProcessing}
              className="w-full h-14 text-lg font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Processing...
                </span>
              ) : amountCents >= 100 ? (
                <>
                  <Heart className="w-5 h-5 mr-2 fill-white" />
                  Donate {formatDollars(amountCents)}
                </>
              ) : (
                "Select an Amount"
              )}
            </Button>

            {checkoutError && (
              <p className="text-center text-sm text-red-600 mt-3">
                {checkoutError}
              </p>
            )}

            <p className="text-center text-sm text-slate-400 mt-3">
              Secure payment processed by Stripe
            </p>

            <OtherGivingMethods />
          </div>

          {/* What is CMC */}
          <div className="px-6 sm:px-10 py-6 sm:py-8 border-b border-slate-100">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">
              What is CMC?
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Campus Missions Conference is Chi Alpha's largest gathering! Every
              four years, Chi Alpha Missionaries get to gather together for
              fellowship and training. It's a transformative experience that
              renews vision, deepens community, and equips missionaries for the
              work God has called them to.
            </p>
          </div>

          {/* Region Funds Section */}
          <RegionFundsSection />

          {/* Why This Matters */}
          <div className="px-6 sm:px-10 py-6 sm:py-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">
              Why Your Gift Matters
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-red-600 pl-4 sm:pl-6">
                <p className="text-lg text-slate-700 leading-relaxed">
                  Many Chi Alpha missionaries serve sacrificially on college
                  campuses, often on tight budgets. The cost of travel,
                  registration, and lodging can prevent them from attending this
                  once-every-four-years gathering.
                </p>
              </div>
              <div className="border-l-4 border-red-600 pl-4 sm:pl-6">
                <p className="text-lg text-slate-700 leading-relaxed">
                  <strong>Your donation goes directly</strong> to helping
                  missionaries who can't afford to attend CMC 2026. Whether it's
                  covering a registration fee, helping with travel costs, or
                  providing full sponsorship — every dollar makes it possible
                  for another missionary to be there.
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setIsDocModalOpen(true)}
                  className="text-red-600 hover:text-red-700 underline font-medium text-lg cursor-pointer"
                >
                  The Word God Gave Sir James for CMC
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Share Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 text-center">
            Help Spread the Word
          </h2>
          <p className="text-slate-600 text-center mb-6">
            Share this page so others can support missionaries too!
          </p>

          {/* Share Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 max-w-xl mx-auto">
            <Button
              onClick={handleShare}
              variant="outline"
              className="h-12 text-base border-slate-200 hover:bg-slate-50 gap-2"
            >
              {showShareSuccess ? (
                <>
                  <Share2 className="w-5 h-5" />
                  Shared!
                </>
              ) : (
                <>
                  <Share2 className="w-5 h-5" />
                  Share
                </>
              )}
            </Button>
            <Button
              onClick={handleShareQRCode}
              variant="outline"
              className="h-12 text-base border-slate-200 hover:bg-slate-50 gap-2"
            >
              <>
                <Share2 className="w-5 h-5" />
                Share QR Code
              </>
            </Button>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={() => setShowQR(!showQR)}
              variant="outline"
              className="h-11 px-6 text-sm border-slate-200 hover:bg-slate-50 gap-2"
            >
              {showQR ? "Hide QR Code" : "Show QR Code"}
            </Button>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="mt-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <QRCodeSVG
                  value={donateUrl}
                  size={200}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Scan to open the donation page
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-sm text-slate-400">
            CMC Go · Campus Missions Conference 2026
          </p>
        </div>
      </div>

      {/* Google Doc Modal — same as WhyInvitationsMatter page */}
      {isDocModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[85dvh] sm:h-[90vh] flex flex-col max-h-[calc(100dvh-16px)]">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b gap-2">
              <h2 className="text-base sm:text-xl font-semibold text-slate-900 truncate">
                The Word God Gave Sir James for CMC
              </h2>
              <button
                onClick={() => setIsDocModalOpen(false)}
                className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden min-h-0">
              <iframe
                src="https://docs.google.com/document/d/1vo23ldjIphJSz7tvj8h3FQoAtREvzUh31A5zJ__M81I/preview?usp=sharing&embedded=true"
                className="w-full h-full border-0"
                title="The Word God Gave Sir James for CMC"
                allow="clipboard-read; clipboard-write"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
